import clsx from 'clsx';
import type { IconType } from 'react-icons';
import { FiMonitor, FiMoon, FiSun } from 'react-icons/fi';

import { Header } from '@/components';
import { type ThemePreference, useThemePreference } from '@/hooks/useThemePreference';

import s from './AppearanceSettingsPage.module.scss';

const THEME_OPTIONS: readonly {
  value: ThemePreference;
  title: string;
  sub: string;
  icon: IconType;
}[] = [
  {
    value: 'system',
    title: 'Auto mode',
    sub: 'Follow this device’s appearance setting.',
    icon: FiMonitor,
  },
  { value: 'light', title: 'Light', sub: 'Always the light theme.', icon: FiSun },
  { value: 'dark', title: 'Dark', sub: 'Always the dark theme.', icon: FiMoon },
];

const AppearanceContent = () => {
  const { preference, isLoaded, setPreference } = useThemePreference();

  return (
    <div className={s.page}>
      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <FiSun />
        </div>
        <div>
          <h2 className={s.detailTitle}>Theme</h2>
          <p className={s.detailSub}>Choose how VesselHaven looks on this account.</p>
        </div>
      </header>

      <div className={s.group} role="radiogroup" aria-label="Theme">
        {THEME_OPTIONS.map(({ value, title, sub, icon: Icon }) => {
          const selected = preference === value;
          return (
            <button
              key={value}
              type="button"
              className={clsx(s.row, selected && s.rowSelected)}
              onClick={() => setPreference(value)}
              disabled={!isLoaded}
              role="radio"
              aria-checked={selected}
            >
              <div className={s.rowIcon}>
                <Icon />
              </div>
              <div className={s.rowText}>
                <div className={s.rowTitle}>{title}</div>
                <div className={s.rowSub}>{sub}</div>
              </div>
              <span className={clsx(s.radio, selected && s.radioChecked)} aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <p className={s.heroHint}>
        The theme is saved to your account, so it follows you to every device you sign in on.
      </p>
    </div>
  );
};

export default function AppearanceSettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  return (
    <>
      {!embedded && <Header title="Appearance" backButton />}
      <AppearanceContent />
    </>
  );
}
